import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../services/supabaseClient';
import { API_URL } from '../config/api';
import {
  User, Mail, Phone, MapPin, CreditCard, Shield, 
  TrendingUp, Wallet, Package, Star, Clock, ChevronRight,
  ShoppingBag, Award, Zap, Loader2, ArrowRight,
  Target, Fingerprint, RefreshCw, ShoppingCart, Sparkles,
  LayoutGrid, Tag, CheckCircle2, Heart
} from 'lucide-react';

const AccountPage = () => {
  const { user, session } = useAuth();
  const [profileData, setProfileData] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [customerBalance, setCustomerBalance] = useState(0);
  const [paidMonths, setPaidMonths] = useState(0);

  useEffect(() => {
    loadAccountData();
  }, [user]);

  const loadAccountData = async () => {
    if (!user) return;
    try {
      setLoading(true);
      
      const [profileRes, paymentsRes] = await Promise.all([
        supabase.from('profiles').select('*').eq('id', user.id).single(),
        supabase.from('customer_payments').select('payment_amount, status').eq('customer_id', user.id)
      ]);

      // Fetch orders via the API (same as OrdersPage) so RLS / auth is consistent
      let fetchedOrders = [];
      try {
        if (session?.access_token) {
          const ordersRes = await fetch(`${API_URL}/api/orders`, {
            headers: { 'Authorization': `Bearer ${session.access_token}` }
          });
          const ordersJson = await ordersRes.json();
          if (ordersJson.success && Array.isArray(ordersJson.orders)) {
            fetchedOrders = ordersJson.orders;
          }
        }
      } catch (e) {
        console.error('Failed to fetch orders for account page:', e);
      }

      if (profileRes.data) setProfileData(profileRes.data);
      setOrders(fetchedOrders);

      if (paymentsRes.data) {
        const paidPayments = paymentsRes.data.filter(p => p.status === 'paid');
        const totalPaid = paidPayments.reduce((sum, p) => sum + (p.payment_amount || 0), 0);
        setCustomerBalance(totalPaid);
        setPaidMonths(paidPayments.length);
      }

    } catch (err) {
      console.error('Error loading account data:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const s = (status || '').toLowerCase();
    if (s === 'delivered') return 'bg-emerald-100 text-emerald-800 border-emerald-200';
    if (s === 'processing' || s === 'confirmed') return 'bg-blue-100 text-blue-800 border-blue-200';
    if (s === 'shipped') return 'bg-purple-100 text-purple-800 border-purple-200';
    if (s === 'cancelled') return 'bg-rose-100 text-rose-800 border-rose-200';
    return 'bg-amber-100 text-amber-800 border-amber-200';
  };

  const tabs = [
    { id: 'overview', label: 'Dashboard Overview', icon: LayoutGrid },
    { id: 'shopping', label: 'My Orders', icon: ShoppingBag },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-light-aurora flex items-center justify-center">
        <div className="flex flex-col items-center gap-4 bg-white/80 backdrop-blur-xl p-8 rounded-3xl border border-slate-200 shadow-xl">
          <div className="relative">
             <Loader2 className="w-12 h-12 text-orange-600 animate-spin" />
             <div className="absolute inset-0 bg-orange-600/10 rounded-full blur-xl animate-pulse" />
          </div>
          <p className="text-xs font-black text-slate-700 uppercase tracking-widest animate-pulse">Loading Account Dashboard...</p>
        </div>
      </div>
    );
  }

  const totalSpent = orders.reduce((sum, order) => sum + (order.total_amount || order.amount || 0), 0);

  return (
    <div className="min-h-screen bg-light-aurora bg-cross-grid font-sans pb-20 pt-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* ── BREADCRUMB & PAGE HEADER ── */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8">
          <div>
            <div className="flex items-center gap-2 text-xs font-black uppercase tracking-widest text-slate-400 mb-2">
              <Link to="/" className="hover:text-orange-600 transition-colors">Home</Link>
              <ChevronRight className="w-3.5 h-3.5 text-slate-300" />
              <span className="text-slate-900 font-extrabold">My Account</span>
            </div>
            <h1 className="text-3xl sm:text-4xl font-black text-slate-900 uppercase tracking-tight italic">
              ACCOUNT <span className="text-orange-600">DASHBOARD</span>
            </h1>
            <p className="text-xs text-slate-500 font-medium mt-1">
              Manage your orders, profile details, and BrightPlanet savings status.
            </p>
          </div>

          {/* Quick Action Navigation Tabs */}
          <div className="bg-white/90 backdrop-blur-md p-1.5 rounded-2xl border border-slate-200 shadow-sm flex items-center gap-1.5 self-start md:self-auto">
            {tabs.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setActiveTab(id)}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-xs font-black uppercase tracking-wider transition-all duration-300 ${
                  activeTab === id
                    ? 'bg-slate-900 text-white shadow-md'
                    : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                }`}
              >
                <Icon className={`w-4 h-4 ${activeTab === id ? 'text-orange-400' : 'text-slate-400'}`} />
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* ── METRIC STATS HEADER CARDS ── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
          
          {/* Card 1: Total Orders */}
          <div className="clay-card p-6 bg-white border border-slate-200 flex items-center justify-between">
            <div>
              <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Total Orders</p>
              <p className="text-2xl font-black text-slate-900 mt-1">{orders.length}</p>
              <p className="text-[10px] font-bold text-orange-600 mt-0.5">₹{totalSpent.toLocaleString()} spent</p>
            </div>
            <div className="w-12 h-12 rounded-2xl bg-orange-100 border border-orange-200 flex items-center justify-center text-orange-600 shrink-0">
              <ShoppingBag className="w-6 h-6" />
            </div>
          </div>

          {/* Card 2: Savings Balance */}
          <div className="clay-card p-6 bg-white border border-slate-200 flex items-center justify-between">
            <div>
              <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Savings Balance</p>
              <p className="text-2xl font-black text-slate-900 mt-1">₹{customerBalance.toLocaleString()}</p>
              <p className="text-[10px] font-bold text-emerald-600 mt-0.5">{paidMonths} installments paid</p>
            </div>
            <div className="w-12 h-12 rounded-2xl bg-emerald-100 border border-emerald-200 flex items-center justify-center text-emerald-600 shrink-0">
              <Wallet className="w-6 h-6" />
            </div>
          </div>

          {/* Card 3: Active Plan */}
          <div className="clay-card p-6 bg-white border border-slate-200 flex items-center justify-between">
            <div>
              <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Saving Plan</p>
              <p className="text-lg font-black text-slate-900 mt-1 truncate max-w-[140px]">{profileData?.saving_plan || '0% EMI Plan'}</p>
              <p className="text-[10px] font-bold text-blue-600 mt-0.5">Active Membership</p>
            </div>
            <div className="w-12 h-12 rounded-2xl bg-blue-100 border border-blue-200 flex items-center justify-center text-blue-600 shrink-0">
              <Zap className="w-6 h-6" />
            </div>
          </div>

          {/* Card 4: Verification Status */}
          <div className="clay-card p-6 bg-white border border-slate-200 flex items-center justify-between">
            <div>
              <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">KYC Status</p>
              <p className="text-lg font-black text-slate-900 mt-1 uppercase">{profileData?.kyc_status || 'Verified'}</p>
              <p className="text-[10px] font-bold text-purple-600 mt-0.5">Secured Account</p>
            </div>
            <div className="w-12 h-12 rounded-2xl bg-purple-100 border border-purple-200 flex items-center justify-center text-purple-600 shrink-0">
              <Shield className="w-6 h-6" />
            </div>
          </div>

        </div>

        {/* ── TAB CONTENT ── */}
        {activeTab === 'overview' ? (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            
            {/* Left Column: Member Profile Card */}
            <div className="lg:col-span-5 space-y-6">
              <div className="glass-panel p-8 rounded-3xl border border-slate-200/90 shadow-sm relative overflow-hidden bg-white/90">
                <div className="flex flex-col items-center text-center pb-6 border-b border-slate-100">
                  <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-orange-500 to-amber-600 flex items-center justify-center text-white text-3xl font-black shadow-lg mb-4 border-2 border-white">
                    {profileData?.name ? profileData.name.charAt(0).toUpperCase() : 'U'}
                  </div>
                  <h3 className="text-xl font-black text-slate-900 uppercase italic">
                    {profileData?.name || user?.name || 'Member Profile'}
                  </h3>
                  <span className="mt-1 px-3 py-0.5 bg-orange-100 text-orange-700 text-[10px] font-black rounded-full border border-orange-200 uppercase tracking-widest">
                    Customer ID: #{profileData?.customer_id || 'BPV-9082'}
                  </span>
                </div>

                {/* Account Info Details */}
                <div className="space-y-4 pt-6">
                  <div className="flex items-center justify-between p-3 rounded-2xl bg-slate-50 border border-slate-200/60">
                    <div className="flex items-center gap-3">
                      <Mail className="w-4 h-4 text-orange-600" />
                      <span className="text-xs font-bold text-slate-500">Email Address</span>
                    </div>
                    <span className="text-xs font-black text-slate-900 truncate max-w-[180px]">{profileData?.email || user?.email || 'N/A'}</span>
                  </div>

                  <div className="flex items-center justify-between p-3 rounded-2xl bg-slate-50 border border-slate-200/60">
                    <div className="flex items-center gap-3">
                      <Phone className="w-4 h-4 text-orange-600" />
                      <span className="text-xs font-bold text-slate-500">Phone Number</span>
                    </div>
                    <span className="text-xs font-black text-slate-900">{profileData?.phone || profileData?.mobile || '+91 73532 97211'}</span>
                  </div>

                  <div className="flex items-center justify-between p-3 rounded-2xl bg-slate-50 border border-slate-200/60">
                    <div className="flex items-center gap-3">
                      <MapPin className="w-4 h-4 text-orange-600" />
                      <span className="text-xs font-bold text-slate-500">Location</span>
                    </div>
                    <span className="text-xs font-black text-slate-900">{profileData?.city || 'Bailhongal'}, {profileData?.state || 'Karnataka'}</span>
                  </div>
                </div>

                <div className="pt-6 space-y-2.5">
                  <Link
                    to="/orders"
                    className="w-full skeuo-btn py-3 rounded-2xl text-white text-xs font-black uppercase tracking-wider flex items-center justify-center gap-2 shadow-md"
                  >
                    <Package className="w-4 h-4" />
                    <span>View All Orders</span>
                  </Link>
                  <Link
                    to="/coupons"
                    className="w-full py-3 rounded-2xl bg-purple-50 hover:bg-purple-100 border border-purple-200 text-purple-700 text-xs font-black uppercase tracking-wider flex items-center justify-center gap-2 transition-colors shadow-xs"
                  >
                    <Tag className="w-4 h-4 text-purple-600" />
                    <span>View Coupons &amp; Offers</span>
                  </Link>
                </div>
              </div>
            </div>

            {/* Right Column: Recent Purchases & Savings Overview */}
            <div className="lg:col-span-7 space-y-6">
              
              {/* Savings Plan Card */}
              <div className="clay-card p-8 bg-white border border-slate-200 relative overflow-hidden">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-orange-100 border border-orange-200 flex items-center justify-center text-orange-600">
                      <TrendingUp className="w-5 h-5" />
                    </div>
                    <div>
                      <h4 className="text-sm font-black text-slate-900 uppercase">BrightPlanet Savings Growth</h4>
                      <p className="text-[11px] font-medium text-slate-500">Monthly Plan Installment Ledger</p>
                    </div>
                  </div>
                  <span className="text-xs font-black text-emerald-600 bg-emerald-50 px-3 py-1 rounded-full border border-emerald-200">
                    {Math.round((paidMonths / 20) * 100)}% Completed
                  </span>
                </div>

                {/* Progress bar */}
                <div className="space-y-2 mb-6">
                  <div className="flex justify-between text-xs font-bold text-slate-700">
                    <span>Paid Installments: {paidMonths} / 20</span>
                    <span>Total Accumulated: ₹{customerBalance.toLocaleString()}</span>
                  </div>
                  <div className="w-full h-3 bg-slate-100 rounded-full overflow-hidden p-0.5 border border-slate-200">
                    <div 
                      className="bg-gradient-to-r from-orange-500 to-amber-500 h-full rounded-full transition-all duration-1000 shadow-sm"
                      style={{ width: `${Math.min((paidMonths / 20) * 100, 100)}%` }}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 pt-4 border-t border-slate-100">
                  <div className="p-3 rounded-2xl bg-slate-50 border border-slate-200/60">
                    <p className="text-[10px] font-bold text-slate-400 uppercase">Saving Plan</p>
                    <p className="text-sm font-black text-slate-900 mt-0.5">{profileData?.saving_plan || 'Monthly Gold Tier'}</p>
                  </div>
                  <div className="p-3 rounded-2xl bg-slate-50 border border-slate-200/60">
                    <p className="text-[10px] font-bold text-slate-400 uppercase">Batch Assignment</p>
                    <p className="text-sm font-black text-slate-900 mt-0.5">{profileData?.batch || 'Batch A-1'}</p>
                  </div>
                </div>
              </div>

              {/* Recent Orders Snapshot */}
              <div className="glass-panel p-8 rounded-3xl border border-slate-200/90 shadow-sm bg-white/90">
                <div className="flex items-center justify-between mb-6 border-b border-slate-100 pb-4">
                  <h4 className="text-sm font-black text-slate-900 uppercase flex items-center gap-2">
                    <ShoppingBag className="w-4 h-4 text-orange-600" /> Recent Purchases
                  </h4>
                  <Link to="/orders" className="text-xs font-black text-orange-600 hover:text-orange-700 flex items-center gap-1">
                    View All <ChevronRight className="w-3.5 h-3.5" />
                  </Link>
                </div>

                {orders.length > 0 ? (
                  <div className="space-y-3">
                    {orders.slice(0, 3).map(order => (
                      <Link
                        key={order.id}
                        to={`/orders/${order.order_number || order.id}`}
                        className="p-4 rounded-2xl bg-slate-50 hover:bg-orange-50/50 border border-slate-200/80 transition-all flex items-center justify-between group"
                      >
                        <div className="flex items-center gap-3.5">
                          <div className="w-10 h-10 rounded-xl bg-white border border-slate-200 flex items-center justify-center text-slate-700 shrink-0 group-hover:scale-105 transition-transform">
                            <Package className="w-5 h-5 text-orange-600" />
                          </div>
                          <div>
                            <p className="text-xs font-black text-slate-900 uppercase">#{order.order_number || order.id.slice(0, 8)}</p>
                            <p className="text-[10px] text-slate-500">
                              {new Date(order.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className="text-xs font-black text-slate-900">₹{(order.total_amount || order.amount || 0).toLocaleString()}</p>
                            <span className={`inline-block text-[9px] font-black uppercase px-2 py-0.5 rounded border ${getStatusBadge(order.status)}`}>
                              {order.status}
                            </span>
                          </div>
                          <ChevronRight className="w-4 h-4 text-slate-400 group-hover:translate-x-1 transition-transform" />
                        </div>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <div className="py-8 text-center text-slate-400 space-y-3">
                    <Package className="w-12 h-12 mx-auto text-slate-300" />
                    <p className="text-xs font-bold text-slate-600">No orders placed yet.</p>
                    <Link to="/" className="inline-block skeuo-btn text-white text-xs font-black px-6 py-2 rounded-xl">
                      Explore Products
                    </Link>
                  </div>
                )}
              </div>

            </div>

          </div>
        ) : (
          /* Shopping & Order History Tab */
          <div className="glass-panel p-8 rounded-3xl border border-slate-200/90 bg-white/90 shadow-sm">
            <div className="flex items-center justify-between mb-6 pb-4 border-b border-slate-100">
              <div>
                <h3 className="text-lg font-black text-slate-900 uppercase flex items-center gap-2">
                  <ShoppingBag className="w-5 h-5 text-orange-600" /> Complete Order History
                </h3>
                <p className="text-xs text-slate-500">All transactions tied to your BrightBazaar account</p>
              </div>
              <Link to="/orders" className="skeuo-btn text-white text-xs font-black px-5 py-2 rounded-xl uppercase tracking-wider">
                Full Orders Portal
              </Link>
            </div>

            {orders.length > 0 ? (
              <div className="space-y-4">
                {orders.map(order => (
                  <div key={order.id} className="p-5 rounded-2xl bg-white border border-slate-200 flex flex-col md:flex-row md:items-center justify-between gap-4 hover:border-orange-300 transition-all shadow-sm">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-2xl bg-orange-50 border border-orange-200 flex items-center justify-center text-orange-600 shrink-0">
                        <Package className="w-6 h-6" />
                      </div>
                      <div>
                        <h4 className="text-sm font-black text-slate-900 uppercase">Order #{order.order_number || order.id}</h4>
                        <p className="text-xs text-slate-500">
                          Placed on {new Date(order.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center justify-between md:justify-end gap-6">
                      <span className={`px-3 py-1 rounded-full text-xs font-black uppercase border ${getStatusBadge(order.status)}`}>
                        {order.status}
                      </span>
                      <p className="text-base font-black text-slate-900">₹{(order.total_amount || order.amount || 0).toLocaleString()}</p>
                      <Link
                        to={`/orders/${order.order_number || order.id}`}
                        className="px-4 py-2 rounded-xl bg-slate-900 hover:bg-orange-600 text-white text-xs font-bold transition-colors"
                      >
                        Details
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="py-16 text-center text-slate-400 space-y-3">
                <Package className="w-16 h-16 mx-auto text-slate-300" />
                <p className="text-sm font-bold text-slate-700">You haven't placed any orders yet.</p>
                <Link to="/" className="inline-block skeuo-btn text-white text-xs font-black px-6 py-2.5 rounded-2xl">
                  Start Shopping Now
                </Link>
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
};

export default AccountPage;
